// English source-of-truth strings. Keep keys dotted + namespaced by route /
// concern so the file diff is reviewable when copy lands later.
const en: Record<string, string> = {
  'app.name': 'Caratuva',
  'locale.label': 'Language',

  'invoice.title': 'Invoice from {seller}',
  'invoice.amount.label': 'Amount due',
  'invoice.lineItems.heading': 'Line items',
  'invoice.lineItem.qty': 'Qty {qty} × {unitPrice}',
  'invoice.cta.signin': 'Sign in to pay',
  'invoice.cta.continue': 'Continue to pay',
  'invoice.dueDate': 'Due {date}',
  'invoice.status.label': 'Status',
  'invoice.error.notFound': 'Invoice not found.',

  'signin.heading': 'Sign in to pay',
  'signin.subheading': 'We will email you a one-time link. No password required.',
  'signin.email.label': 'Email',
  'signin.email.placeholder': 'you@company.com',
  'signin.submit': 'Send sign-in link',
  'signin.tos':
    'By continuing, you agree to our <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.',
  'signin.success.heading': 'Check your email',
  'signin.success.body': 'We sent a sign-in link. It expires in 15 minutes.',
  'signin.error.emailMismatch': 'That email does not match the invoice recipient.',
  'signin.error.generic': 'Could not send the sign-in link. Try again in a moment.',
  'signin.session.expired': 'Your session has expired. Please sign in again.',

  'consume.loading': 'Signing you in...',
  'consume.error.invalid': 'This sign-in link is invalid.',
  'consume.error.used': 'This sign-in link has already been used. Request a new one.',
  'consume.error.expired': 'This sign-in link has expired. Request a new one.',
  'consume.error.generic': 'Could not sign you in. Request a new sign-in link.',
  'consume.cta.requestNew': 'Request a new link',

  // KYC
  'kyc.heading': 'Verify your identity',
  'kyc.subheading':
    'Complete a one-time identity check so we can release the on-ramp for this invoice.',
  'kyc.step.entityType': 'Account type',
  'kyc.step.tos': 'Terms of service',
  'kyc.step.personal': 'Personal info',
  'kyc.step.address': 'Address',
  'kyc.step.documents': 'Identity documents',
  'kyc.step.business': 'Business info',
  'kyc.step.businessAddress': 'Business address',
  'kyc.step.owners': 'Beneficial owners',
  'kyc.step.businessDocuments': 'Business documents',
  'kyc.step.review': 'Review',
  'kyc.step.indicator': 'Step {current} of {total}',
  // Entity type chooser
  'kyc.entityType.heading': 'Are you completing this on behalf of yourself or a business?',
  'kyc.entityType.body':
    'Pick the option that matches who is paying. Businesses need to provide company documents and beneficial-owner information; individuals only need their personal ID.',
  'kyc.entityType.individual': 'Individual',
  'kyc.entityType.individual.body':
    'You are paying as a private person, not on behalf of a company.',
  'kyc.entityType.business': 'Business',
  'kyc.entityType.business.body':
    'You are paying on behalf of a registered company or other legal entity.',
  // Business fields
  'kyc.field.legalName.label': 'Legal company name',
  'kyc.field.legalName.placeholder': 'Caratuva Inc.',
  'kyc.field.formationDate.label': 'Date of formation',
  'kyc.field.website.label': 'Company website',
  'kyc.field.website.placeholder': 'https://example.com',
  'kyc.field.businessTaxId.label': 'Business tax ID',
  'kyc.field.businessTaxId.placeholder': '12-3456789 / 12.345.678/0001-95',
  'kyc.field.businessTaxId.hint': 'EIN (US), CNPJ (BR), or local equivalent.',
  'kyc.field.incorporationDocFile.label': 'Incorporation document',
  'kyc.field.incorporationDocFile.hint':
    'Articles of incorporation, certificate of formation, or equivalent.',
  'kyc.field.proofOfOwnershipDocFile.label': 'Proof of ownership',
  'kyc.field.proofOfOwnershipDocFile.hint':
    'Cap table, ownership register, or equivalent showing the listed beneficial owners.',
  // Owners
  'kyc.owners.heading': 'Beneficial owners',
  'kyc.owners.body':
    'Add anyone who owns 25% or more of the company, or who exercises significant control. Each owner needs the same identity check as an individual buyer.',
  'kyc.owners.add': 'Add another owner',
  'kyc.owners.remove': 'Remove',
  'kyc.owners.title': 'Owner {index}',
  'kyc.owners.role.label': 'Role',
  'kyc.owners.role.placeholder': 'Select a role',
  'kyc.owners.role.beneficial_controlling': 'Beneficial owner & controlling person',
  'kyc.owners.role.beneficial_owner': 'Beneficial owner',
  'kyc.owners.role.controlling_person': 'Controlling person',
  'kyc.owners.taxType.label': 'Tax ID type (US only)',
  'kyc.owners.taxType.placeholder': 'Select a tax ID type',
  'kyc.owners.taxType.SSN': 'SSN',
  'kyc.owners.taxType.ITIN': 'ITIN',
  'kyc.owners.proofOfAddress.label': 'Proof of address',
  'kyc.owners.proofOfAddress.hint':
    'Document showing the owner’s residential address (issued in the last 90 days).',
  'kyc.owners.proofOfAddressType.label': 'Proof of address type',
  'kyc.owners.proofOfAddressType.placeholder': 'Select a document type',
  'kyc.owners.proofOfAddressType.UTILITY_BILL': 'Utility bill',
  'kyc.owners.proofOfAddressType.BANK_STATEMENT': 'Bank statement',
  'kyc.owners.proofOfAddressType.RENTAL_AGREEMENT': 'Rental agreement',
  'kyc.owners.proofOfAddressType.TAX_DOCUMENT': 'Tax document',
  'kyc.owners.proofOfAddressType.GOVERNMENT_CORRESPONDENCE': 'Government correspondence',
  'kyc.tos.heading': 'Accept the settlement partner terms of service',
  'kyc.tos.body':
    'You will be redirected to our settlement partner to review and accept the terms of service. After acceptance, we will bring you back here to continue.',
  'kyc.tos.cta': 'Continue to terms of service',
  'kyc.tos.cta.pending': 'Opening settlement partner...',
  'kyc.tos.accepted': 'Terms of service accepted.',
  'kyc.tos.required': 'Please accept the terms of service before continuing.',
  'kyc.tos.error': 'Could not start the terms-of-service flow. Try again in a moment.',
  'kyc.field.email.label': 'Email',
  'kyc.field.firstName.label': 'First name',
  'kyc.field.firstName.placeholder': 'Maria',
  'kyc.field.lastName.label': 'Last name',
  'kyc.field.lastName.placeholder': 'Silva',
  'kyc.field.dateOfBirth.label': 'Date of birth',
  'kyc.field.taxId.label': 'Tax ID',
  'kyc.field.taxId.placeholder': '123-45-6789 / 123.456.789-00',
  'kyc.field.taxId.hint': 'SSN/EIN (US), CPF (BR), or local equivalent.',
  'kyc.field.country.label': 'Country of residence',
  'kyc.field.country.placeholder': 'Select a country',
  'kyc.field.phone.label': 'Phone (optional)',
  'kyc.field.phone.placeholder': '+1 555 123 4567',
  'kyc.field.addressLine1.label': 'Address line 1',
  'kyc.field.addressLine1.placeholder': 'Street and number',
  'kyc.field.addressLine2.label': 'Address line 2 (optional)',
  'kyc.field.addressLine2.placeholder': 'Apt, suite, unit',
  'kyc.field.city.label': 'City',
  'kyc.field.stateProvinceRegion.label': 'State / Province / Region',
  'kyc.field.postalCode.label': 'Postal code',
  'kyc.field.idDocCountry.label': 'Document issuing country',
  'kyc.field.idDocType.label': 'Document type',
  'kyc.field.idDocType.placeholder': 'Select a document type',
  'kyc.field.idDocType.passport': 'Passport',
  'kyc.field.idDocType.id_card': 'National ID card',
  'kyc.field.idDocType.drivers': 'Driver’s license',
  'kyc.field.idDocNumber.label': 'Document number (optional)',
  'kyc.field.selfieFile.label': 'Selfie',
  'kyc.field.selfieFile.hint':
    'Take a clear photo of your face — no glasses, no hat, well-lit. JPG or PNG, max 8 MB.',
  'kyc.field.idDocFrontFile.label': 'Document — front',
  'kyc.field.idDocFrontFile.hint':
    'Photo of the front of your document, all four corners visible. JPG or PNG, max 8 MB.',
  'kyc.field.idDocBackFile.label': 'Document — back',
  'kyc.field.idDocBackFile.hint':
    'Required for ID cards and driver’s licenses. JPG or PNG, max 8 MB.',
  'kyc.upload.cta': 'Choose file',
  'kyc.upload.replace': 'Replace',
  'kyc.upload.uploading': 'Uploading...',
  'kyc.upload.uploaded': 'Uploaded',
  'kyc.upload.error': 'Upload failed. Try a different file.',
  'kyc.upload.unsupported': 'Use JPG, PNG, or WebP under 8 MB.',
  'kyc.upload.preview': 'Preview',
  'kyc.error.required': 'This field is required.',
  'kyc.error.email': 'Enter a valid email address.',
  'kyc.error.country': 'Use the ISO-3166-1 alpha-2 code (e.g. US, BR, FR).',
  'kyc.error.dateOfBirth': 'Enter a valid date of birth.',
  'kyc.error.minor': 'You must be 18 or older.',
  'kyc.error.generic': 'Could not start your identity check. Try again in a moment.',
  'kyc.review.heading': 'Identity check submitted',
  'kyc.review.body':
    'Your identity check is under review. We will email you when it has been processed.',
  'kyc.review.cta': 'View payment status',
  'kyc.review.summary.heading': 'Review your information',
  'kyc.review.summary.body':
    'Take a moment to confirm everything looks right. Once submitted, you cannot edit a KYC submission — you would need to start a new one.',
  'kyc.submit': 'Submit identity check',
  'kyc.submit.pending': 'Submitting...',
  'kyc.next': 'Continue',
  'kyc.back': 'Back',
  'kyc.status.label': 'Status',
  'kyc.status.verifying': 'Under review',
  'kyc.status.approved': 'Approved',
  'kyc.status.rejected': 'Rejected',
  'kyc.status.notStarted': 'Not started',
  'kyc.hosted.cta': 'Continue to identity provider',

  // On-ramp
  'onramp.heading': 'Choose how you want to pay',
  'onramp.subheading': 'We lock an FX quote for 10 minutes once you select a method.',
  'onramp.method.ach': 'ACH (US bank transfer)',
  'onramp.method.wire': 'Wire transfer',
  'onramp.method.sepa': 'SEPA (EU bank transfer)',
  'onramp.method.card': 'Card',
  'onramp.method.label': 'Funding method',
  'onramp.targetCurrency.label': 'Currency you will send',
  'onramp.targetCurrency.helper':
    'We convert at the locked rate when funds arrive. Match this to the account you are sending from.',
  'onramp.quote.locked': 'Quote locked. Expires {expires}.',
  'onramp.quote.expired': 'The quote expired. Refresh to lock a new one.',
  'onramp.quote.refresh': 'Refresh quote',
  'onramp.cta.lockQuote': 'Lock FX quote',
  'onramp.cta.confirm': 'Confirm and get instructions',
  'onramp.cta.confirm.pending': 'Submitting...',
  'onramp.error.generic': 'Could not initiate the on-ramp. Try again in a moment.',
  'onramp.instructions.heading': 'Send your payment',
  'onramp.instructions.subheading':
    'This account is unique to your invoice — send the exact amount and we match your transfer automatically.',
  'onramp.instructions.bank': 'Bank',
  'onramp.instructions.accountName': 'Account name',
  'onramp.instructions.accountNumber': 'Account number',
  'onramp.instructions.routing': 'Routing number',
  'onramp.instructions.iban': 'IBAN',
  'onramp.instructions.swift': 'SWIFT / BIC',
  'onramp.instructions.reference': 'Reference code',
  'onramp.instructions.memo': 'Memo',
  'onramp.instructions.copy': 'Copy',
  'onramp.instructions.copied': 'Copied',
  'onramp.instructions.continue': 'Track this payment',
  'onramp.instructions.empty':
    'No banking instructions returned yet. We will email them when ready.',

  // Tracking
  'tracking.heading': 'Payment tracking',
  'tracking.placeholder': 'KYC, funding-method, and on-ramp screens land in follow-up PRs.',
  'tracking.cta.back': 'Back to invoice',
  'tracking.cta.startOnRamp': 'Continue to pay',
  'tracking.refreshing': 'Polling for updates...',
  'tracking.timeline.heading': 'Progress',
  'tracking.step.invoice': 'Invoice received',
  'tracking.step.kyc': 'Identity check complete',
  'tracking.step.onramp': 'On-ramp initiated',
  'tracking.step.fiat': 'Funds received',
  'tracking.step.offramp': 'Off-ramp initiated',
  'tracking.step.settled': 'Settled in BRL',
  'tracking.alert.kycRejected.heading': 'Identity check rejected',
  'tracking.alert.kycRejected.body':
    'Your identity check could not be approved. Resubmit with updated details.',
  'tracking.alert.kycRejected.cta': 'Resubmit identity check',
  'tracking.alert.kycPending.heading': 'Identity check under review',
  'tracking.alert.kycPending.body':
    'We are waiting on the verification provider. We will email you when it completes.',
  'tracking.alert.underpayment.heading': 'Underpayment detected',
  'tracking.alert.underpayment.body':
    'The amount we received is less than the invoice. Contact support to reconcile.',
  'tracking.alert.contactSupport': 'Contact support',
  'tracking.alert.expired.heading': 'Invoice expired',
  'tracking.alert.expired.body': 'This invoice has expired. Ask the seller to issue a fresh one.',
  'tracking.alert.cancelled.heading': 'Invoice cancelled',
  'tracking.alert.cancelled.body': 'The seller cancelled this invoice.',

  'common.loading': 'Loading...',
  'common.retry': 'Retry',
};
export default en;
